import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Button, message, Row, Col, Typography, Select } from 'antd';
import { Landmark } from 'lucide-react';
import { Method_POST_CONTEXTO } from '../../../../../service/api';

const { Title, Text } = Typography;

export default function ContaBancariaModal({ open, onClose, onSave, initialData = null }) {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [bancos, setBancos] = useState([]);
    const [loadingBancos, setLoadingBancos] = useState(false);

    useEffect(() => {
        if (open) {
            fetchBancos();
            if (initialData) {
                form.setFieldsValue({
                    bancoId: initialData.banco?.id,
                    agencia: initialData.agencia,
                    agenciaDV: initialData.agenciaDV,
                    conta: initialData.conta,
                    contaDV: initialData.contaDV
                });
            } else {
                form.resetFields();
            }
        }
    }, [open, initialData, form]);

    const fetchBancos = async () => {
        setLoadingBancos(true);
        try {
            const res = await Method_POST_CONTEXTO("/cliente/conta-bancaria/banco/listar");
            const data = Array.isArray(res) ? res : [];

            data.sort((a, b) => (a.nome || "").localeCompare(b.nome || ""));

            const options = data.map(b => ({
                value: b.id,
                label: `${b.codigoFEBRABAN} - ${b.nome}`
            }));
            
            // Adiciona a opção de Caixa no topo da lista
            setBancos([
                { value: 0, label: '000 - DINHEIRO (CAIXA)' },
                ...options
            ]);
        } catch (error) {
            console.error("Erro ao carregar bancos:", error);
        } finally {
            setLoadingBancos(false);
        }
    };

    const handleFinish = async (values) => {
        setLoading(true);
        try {
            const company = sessionStorage.getItem("COMPANY");
            const clienteId = company ? JSON.parse(company).id : null;

            if (!clienteId) {
                message.error("Sessão expirada. Por favor, faça login novamente.");
                return;
            }

            const payload = {
                id: initialData?.id,
                cliente: { id: clienteId },
                banco: { id: values.bancoId },
                agencia: values.agencia,
                agenciaDV: values.agenciaDV || "",
                conta: values.conta,
                contaDV: values.contaDV || "",
                // Preserva os IDs do Pluggy (Open Finance) para não perdê-los na atualização
                pluggyAccountId: initialData?.pluggyAccountId,
                pluggyConnectionId: initialData?.pluggyConnectionId,
            };

            const endpoint = initialData ? "/cliente/conta-bancaria/atualizar" : "/cliente/conta-bancaria/cadastrar";
            await Method_POST_CONTEXTO(endpoint, payload);

            message.success(`Conta bancária ${initialData ? "atualizada" : "cadastrada"} com sucesso!`);
            form.resetFields();
            onSave();
            onClose();
        } catch (error) {
            console.error(error);
            message.error(`Erro ao ${initialData ? "atualizar" : "cadastrar"} conta bancária.`);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            open={open}
            onCancel={onClose}
            footer={null}
            width={650}
            className="premium-modal-v2"
            destroyOnClose
        >
            <div style={{ padding: '8px' }}>
                <header style={{
                    marginBottom: 32,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 16,
                    borderBottom: '1px solid #f1f5f9',
                    paddingBottom: 20,
                    flexWrap: 'wrap'
                }}>
                    <div style={{
                        width: 48,
                        height: 48,
                        borderRadius: 12,
                        backgroundColor: '#eff6ff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#2563eb',
                        fontSize: 24,
                        boxShadow: '0 4px 12px rgba(37, 99, 235, 0.1)',
                        flexShrink: 0
                    }}>
                        <Landmark size={24} />
                    </div>
                    <div style={{ flex: 1, minWidth: '200px' }}>
                        <Title level={4} style={{ margin: 0, color: '#0f172a', fontWeight: 700 }}>
                            {initialData ? 'Editar Conta Bancária' : 'Nova Conta Bancária'}
                        </Title>
                        <Text type="secondary">Preencha os dados da conta conforme seu extrato bancário.</Text>
                    </div>
                </header>

                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleFinish}
                    requiredMark={false}
                    initialValues={{ bancoId: undefined }}
                >
                    <Row gutter={[16, 0]}>
                        <Col xs={24} sm={20} md={16}>
                            <Form.Item
                                name="bancoId"
                                label="Instituição Financeira (Banco)"
                                rules={[{ required: true, message: 'Selecione o banco' }]}
                            >
                                <Select
                                    showSearch
                                    placeholder="Selecione ou busque pelo nome/código"
                                    optionFilterProp="children"
                                    size="large"
                                    loading={loadingBancos}
                                    options={bancos}
                                    className="premium-select"
                                    onChange={(val) => {
                                        // Se selecionar Caixa (ID 0), preenche campos padrão
                                        if (val === 0) {
                                            form.setFieldsValue({
                                                agencia: '0000',
                                                agenciaDV: '0',
                                                conta: '0',
                                                contaDV: '0'
                                            });
                                        }
                                    }}
                                    filterOption={(input, option) =>
                                        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                    }
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col xs={18} sm={10}>
                            <Form.Item
                                name="agencia"
                                label="Agência"
                                rules={[{ required: true, message: 'Obrigatorio' }]}
                            >
                                <Input placeholder="Número da agência" size="large" style={{ borderRadius: 10 }} maxLength={5} />
                            </Form.Item>
                        </Col>
                        <Col xs={6} sm={4}>
                            <Form.Item
                                name="agenciaDV"
                                label="Dig."
                            >
                                <Input placeholder="DV" size="large" style={{ borderRadius: 10 }} maxLength={1} />
                            </Form.Item>
                        </Col>
                        <Col xs={18} sm={6}>
                            <Form.Item
                                name="conta"
                                label="Número da Conta"
                                rules={[{ required: true, message: 'Obrigatorio' }]}
                            >
                                <Input placeholder="Conta" size="large" style={{ borderRadius: 10 }} maxLength={12} />
                            </Form.Item>
                        </Col>
                        <Col xs={6} sm={4}>
                            <Form.Item
                                name="contaDV"
                                label="Dig."
                            >
                                <Input placeholder="DV" size="large" style={{ borderRadius: 10 }} maxLength={1} />
                            </Form.Item>
                        </Col>
                    </Row>

                    <div style={{
                        marginTop: 40,
                        display: 'flex',
                        justifyContent: 'flex-end',
                        gap: 12,
                        paddingTop: 24,
                        borderTop: '1px solid #f1f5f9',
                        flexWrap: 'wrap-reverse'
                    }}>
                        <Button
                            onClick={onClose}
                            size="large"
                            style={{ borderRadius: 10, flex: 1, minWidth: '120px' }}
                        >
                            Cancelar
                        </Button>
                        <Button
                            type="primary"
                            htmlType="submit"
                            loading={loading}
                            size="large"
                            style={{
                                background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
                                borderColor: '#2563eb',
                                fontWeight: 600,
                                padding: '0 32px',
                                borderRadius: 10,
                                flex: 2,
                                minWidth: '180px'
                            }}
                        >
                            {initialData ? 'Salvar Alterações' : 'Cadastrar Conta'}
                        </Button>
                    </div>
                </Form>
            </div>
        </Modal>
    );
}
